import { getAuth } from 'firebase/auth';
import { config } from '../config';

// ---------------------------------------------------------------------------
// Server → Client message types
// ---------------------------------------------------------------------------

type ServerMessageType =
  | 'session_started'
  | 'item_evaluated'
  | 'item_followup'
  | 'session_completed'
  | 'discovery_turn'
  | 'all_items_covered'
  | 'phase_changed'
  | 'question_detected'
  | 'stored_answer_generated'
  | 'answer_generated'
  | 'landmine_alert'
  | 'competitor_alert'
  | 'speech_rate_alert'
  | 'error';

/** Phase machine state on the server side. Surfaced via `phase_changed`
 *  events so the UI can render a small banner showing which phase the
 *  AI customer is in. The rep leads first (`rep_lead`); saying "do you have
 *  any questions for me?" hands off to the AI's KB questions (`kb_phase`). */
export type ConversationPhase =
  | 'rep_lead'
  | 'kb_phase'
  | 'close_phase'
  | 'free_chat';

export interface PhaseChangedData {
  old_phase: ConversationPhase | null;
  new_phase: ConversationPhase;
  turn: number;
}

/** Emitted by the training simulator immediately after the AI customer
 *  surfaces an objection/FAQ KB item — built locally from the track item's
 *  contentSnapshot, no production_final_server roundtrip. Same payload
 *  shape as the live-call event so the live-call ConversationEntry /
 *  Redux dispatch path can be reused unchanged. */
export interface QuestionDetectedData {
  question: string;
  category: string;
  relevance_score: number;
  timestamp: string;
  sourceId?: string;
  parentId?: string | null;
}

export interface StoredAnswerData {
  question: string;
  stored_answer: string;
  category: string;
  timestamp: string;
}

interface TrainingMessage {
  type: ServerMessageType;
  data?: any;
}

// ---------------------------------------------------------------------------
// Shared sub-types
// ---------------------------------------------------------------------------

interface DetectedObjection {
  type: string;
  category: string;
  trigger?: string;
  competitor?: string;
  text: string;
  landmine_type?: 'legal_risk' | 'detractor';
  can_say?: Array<any>;
  cannot_say?: Array<any>;
  quick_tips?: string;
  advantages?: Array<any>;
  weaknesses?: Array<any>;
  talking_points?: string;
}

// ---------------------------------------------------------------------------
// Server → Client payloads
// ---------------------------------------------------------------------------

export interface SessionStartedData {
  sessionId: string;
  trackName: string;
  itemCount: number;
  difficulty: string;
  pushbackLevel: string;
  hasPlaybook: boolean;
  playbookIndustry?: string;
  playbookRoleName?: string;
}

export interface TrainingItem {
  trackItemId: string;
  itemType: string;
  aiText: string;
  aiAudioBase64?: string;
  expectedKeywords?: string[];
  landmineKeywords?: string[];
  allowedResponseTime: number;
  detectedObjections?: DetectedObjection[];
  competitorsMentioned?: string[];
  landminesTriggered?: number;
  /** @deprecated use storedAnswer */
  idealAnswer?: string;
  storedAnswer?: string;
  aiGeneratedAnswer?: string;
}

export interface ItemEvaluation {
  trackItemId: string;
  itemType?: string;
  responseQuality: number;
  keywordsCovered: string[];
  keywordsMissed: string[];
  landminesTriggered: number;
  competitorsMentioned?: string[];
  detectedObjections?: DetectedObjection[];
  aiEvaluation: string;
  aiFollowUp?: string;
  aiFollowUpAudioBase64?: string;
}

export interface ItemFollowup {
  trackItemId: string;
  round: number;
  aiText: string;
  aiAudioBase64?: string;
  previousScore: number;
  feedback: string;
}

export interface DiscoveryTurnData {
  aiText: string;
  aiAudioBase64?: string;
  /** True when this turn is a qualifying question with no KB item to evaluate. */
  isQualifyingTurn: boolean;
  turnNumber: number;
  /** Present only when the AI surfaced a knowledge-base item in this turn. */
  surfacedItem?: {
    trackItemId: string;
    itemType: string;
    storedAnswer?: string;
    aiGeneratedAnswer?: string;
    expectedKeywords?: string[];
    detectedObjections?: DetectedObjection[];
  };
}

export interface AllItemsCoveredData {
  itemsCovered: number;
  totalItems: number;
}

export interface SpeechRateAlert {
  message: string;
  wordsPerMinute: number;
}

export interface SessionResult {
  sessionId: string;
  itemsCompleted?: number;
  totalItems?: number;
  itemResults: ItemEvaluation[];
  completedAt: string;
  playbookCoverage?: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Service class
// ---------------------------------------------------------------------------

class TrainingWebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 2000;
  private isManualDisconnect = false;
  private currentTrackId: string | null = null;
  private currentSessionId: string | null = null;

  // Event handlers — server → client
  private onSessionStartedHandler: ((data: SessionStartedData) => void) | null = null;
  private onItemEvaluatedHandler: ((evaluation: ItemEvaluation) => void) | null = null;
  private onItemFollowupHandler: ((followup: ItemFollowup) => void) | null = null;
  private onSessionCompletedHandler: ((result: SessionResult) => void) | null = null;
  private onDiscoveryTurnHandler: ((data: DiscoveryTurnData) => void) | null = null;
  private onAllItemsCoveredHandler: ((data: AllItemsCoveredData) => void) | null = null;
  private onPhaseChangedHandler: ((data: PhaseChangedData) => void) | null = null;
  private onQuestionDetectedHandler: ((data: QuestionDetectedData) => void) | null = null;
  private onStoredAnswerGeneratedHandler: ((data: StoredAnswerData) => void) | null = null;
  private onLandmineAlertHandler: ((data: any) => void) | null = null;
  private onCompetitorAlertHandler: ((data: any) => void) | null = null;
  private onSpeechRateAlertHandler: ((data: SpeechRateAlert) => void) | null = null;
  private onErrorHandler: ((error: string) => void) | null = null;
  private onConnectionChangeHandler: ((connected: boolean) => void) | null = null;

  // ---------------------------------------------------------------------------
  // Connection management
  // ---------------------------------------------------------------------------

  connect(trackId: string, sessionId?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isManualDisconnect = false;
      this.currentTrackId = trackId;

      const wsUrl = config.server.trainingUrl;

      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = async () => {
        this.reconnectAttempts = 0;
        this.onConnectionChangeHandler?.(true);

        try {
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();

          if (!token) {
            throw new Error('No authentication token available');
          }

          this.socket?.send(
            JSON.stringify({
              type: 'start_session',
              data: { trackId, token, sessionId },
            })
          );
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message: TrainingMessage = JSON.parse(event.data);
          this.handleServerMessage(message);
        } catch (error) {
          // Non-fatal parse error — log and continue
          console.error('TrainingWebSocketService: failed to parse message', error);
        }
      };

      this.socket.onerror = (error) => {
        this.onConnectionChangeHandler?.(false);
        reject(error);
      };

      this.socket.onclose = () => {
        this.onConnectionChangeHandler?.(false);

        if (!this.isManualDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = this.reconnectDelay * this.reconnectAttempts;
          setTimeout(() => {
            if (this.currentTrackId) {
              this.connect(this.currentTrackId).catch(() => {
                // Reconnect failure is logged internally; callers rely on onConnectionChange
              });
            }
          }, delay);
        }
      };

      setTimeout(() => {
        if (this.socket?.readyState !== WebSocket.OPEN) {
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    this.currentTrackId = null;
    this.currentSessionId = null;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.onConnectionChangeHandler?.(false);
  }

  // ---------------------------------------------------------------------------
  // Message dispatch
  // ---------------------------------------------------------------------------

  private handleServerMessage(message: TrainingMessage): void {
    switch (message.type) {
      case 'session_started':
        this.currentSessionId = message.data?.sessionId ?? null;
        this.onSessionStartedHandler?.(message.data);
        break;

      case 'item_evaluated':
        this.onItemEvaluatedHandler?.(message.data);
        break;

      case 'item_followup':
        this.onItemFollowupHandler?.(message.data);
        break;

      case 'session_completed':
        this.onSessionCompletedHandler?.(message.data);
        break;

      case 'discovery_turn':
        this.onDiscoveryTurnHandler?.(message.data);
        break;

      case 'all_items_covered':
        this.onAllItemsCoveredHandler?.(message.data);
        break;

      case 'phase_changed':
        this.onPhaseChangedHandler?.(message.data);
        break;

      case 'question_detected':
        // Emitted locally by the training simulator when the AI customer
        // surfaces an Objection/FAQ KB item (no detection service involved).
        this.onQuestionDetectedHandler?.(message.data);
        break;

      case 'stored_answer_generated':
        // Canonical answer from the KB item's contentSnapshot.answer field.
        this.onStoredAnswerGeneratedHandler?.(message.data);
        break;

      case 'answer_generated':
        // Training does not emit AI-generated alternative answers — only
        // the canonical stored answer. This case is kept solely for
        // ServerMessageType exhaustiveness; the payload is dropped if it
        // somehow arrives (e.g. from a stale production_final response).
        break;

      case 'landmine_alert':
        // Emitted by training_simulation.run_detections when the landmine
        // detection service finds a hit on either the AI customer's or
        // the rep's transcript. Routed through to TrainingSessionPage,
        // which dispatches the live-call `addLandmineAlert` Redux action
        // so LandmineAlertsContainer renders it identically to live calls.
        this.onLandmineAlertHandler?.(message.data);
        break;

      case 'competitor_alert':
        // Same as landmine_alert above — forwarded to TrainingSessionPage
        // for live-call-style CompetitorAlert rendering.
        this.onCompetitorAlertHandler?.(message.data);
        break;

      case 'speech_rate_alert':
        this.onSpeechRateAlertHandler?.(message.data);
        break;

      case 'error':
        this.onErrorHandler?.(message.data?.message ?? 'Unknown training error');
        break;

      default: {
        // Exhaustiveness guard: TypeScript narrows to never here if all cases are handled
        const _exhaustive: never = message.type;
        console.warn('TrainingWebSocketService: unhandled message type', _exhaustive);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Client → Server messages
  // ---------------------------------------------------------------------------

  /** Sent ~2s after session_started to trigger first item delivery. */
  sendReady(): void {
    this.sendRaw({ type: 'ready', data: {} });
  }

  /** Standard response to a delivered item (also triggers auto-advance on silence). */
  submitAndNext(trackItemId: string, userResponse: string, responseTime: number): void {
    this.sendRaw({
      type: 'submit_and_next',
      data: { trackItemId, userResponse, responseTime },
    });
  }

  /** Response to an AI pushback round (item_followup). Does not advance item index. */
  submitFollowupResponse(trackItemId: string, userResponse: string, responseTime: number): void {
    this.sendRaw({
      type: 'submit_followup_response',
      data: { trackItemId, userResponse, responseTime },
    });
  }

  /** Legacy alias kept for any callers that used the old submit_response path. */
  submitResponse(trackItemId: string, userResponse: string, responseTime: number): void {
    this.submitAndNext(trackItemId, userResponse, responseTime);
  }

  endSession(): void {
    this.sendRaw({ type: 'end_session', data: {} });
  }

  /** Generic escape hatch for one-off messages not covered above. */
  sendMessage(message: { type: string; data: any }): void {
    this.sendRaw(message);
  }

  private sendRaw(message: { type: string; data: any }): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('TrainingWebSocketService: cannot send — socket not open', message.type);
      return;
    }
    this.socket.send(JSON.stringify(message));
  }

  // ---------------------------------------------------------------------------
  // Event handler setters
  // ---------------------------------------------------------------------------

  onSessionStarted(handler: (data: SessionStartedData) => void): void {
    this.onSessionStartedHandler = handler;
  }

  onItemEvaluated(handler: (evaluation: ItemEvaluation) => void): void {
    this.onItemEvaluatedHandler = handler;
  }

  onItemFollowup(handler: (followup: ItemFollowup) => void): void {
    this.onItemFollowupHandler = handler;
  }

  onSessionCompleted(handler: (result: SessionResult) => void): void {
    this.onSessionCompletedHandler = handler;
  }

  onDiscoveryTurn(handler: (data: DiscoveryTurnData) => void): void {
    this.onDiscoveryTurnHandler = handler;
  }

  onAllItemsCovered(handler: (data: AllItemsCoveredData) => void): void {
    this.onAllItemsCoveredHandler = handler;
  }

  /** Fires every time the AI customer's phase changes (rep_lead → kb → close).
   *  The UI uses this to render a small banner. The first call (turn 0) fires
   *  the moment the conversation starts with the initial phase. */
  onPhaseChanged(handler: (data: PhaseChangedData) => void): void {
    this.onPhaseChangedHandler = handler;
  }

  /** Fires when the AI customer surfaces an Objection/FAQ KB item. The
   *  data is built directly from the in-memory track item by the
   *  training simulator — no detection service involved. */
  onQuestionDetected(handler: (data: QuestionDetectedData) => void): void {
    this.onQuestionDetectedHandler = handler;
  }

  /** Fires immediately after `onQuestionDetected` when the KB item has a
   *  canonical answer stored in its contentSnapshot. The rep sees this
   *  as the "correct" answer to practise delivering. */
  onStoredAnswerGenerated(handler: (data: StoredAnswerData) => void): void {
    this.onStoredAnswerGeneratedHandler = handler;
  }

  /** Fires when the landmine detection service flags a hit on either
   *  speaker's transcript. The payload matches the live-call shape — see
   *  ConversationPage's `addLandmineAlert` dispatch for reference. */
  onLandmineAlert(handler: (data: any) => void): void {
    this.onLandmineAlertHandler = handler;
  }

  /** Fires when the competitor detection service flags a competitor
   *  mention on either speaker's transcript. */
  onCompetitorAlert(handler: (data: any) => void): void {
    this.onCompetitorAlertHandler = handler;
  }

  onSpeechRateAlert(handler: (data: SpeechRateAlert) => void): void {
    this.onSpeechRateAlertHandler = handler;
  }

  onError(handler: (error: string) => void): void {
    this.onErrorHandler = handler;
  }

  onConnectionChange(handler: (connected: boolean) => void): void {
    this.onConnectionChangeHandler = handler;
  }

  // ---------------------------------------------------------------------------
  // Accessors
  // ---------------------------------------------------------------------------

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const trainingWebSocketService = new TrainingWebSocketService();
